import { doc, onSnapshot } from "firebase/firestore";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import CookieConsent from "../components/CookieConsent";
import SEO from "../components/SEO";
import { db } from "../firebase";
import { useScrollReveal } from "../hooks/useScrollReveal";
import "../styles/Home.css";

const defaultSlides = [
  {
    backgroundImage: "/assets/slide1.jpg",
    title: "Innovative Mobile Gaming Solutions",
    description:
      "Pioneering the future of mobile gaming with immersive, high-performance experiences.",
  },
];

const defaultAbout = {
  title: "Welcome to MACROMATRIX (PRIVATE) LIMITED",
  content:
    "We specialize in developing cutting-edge cross-platform applications designed to enrich user experiences.",
};

const defaultOurStory = {
  title: "Our Story",
  content:
    "At MACROMATRIX (PRIVATE) LIMITED, we believe in pushing the boundaries of technology to create innovative apps and games...",
};

const featureItems = [
  {
    title: "Cross-Platform Delivery",
    text: "Build once and launch on both Android and iOS with performance-focused architecture.",
  },
  {
    title: "Creative Game Design",
    text: "From casual to action titles, we craft gameplay systems that improve retention and engagement.",
  },
  {
    title: "Scalable LiveOps",
    text: "Analytics-ready and update-friendly products for long-term growth and monetization.",
  },
];

const stats = [
  { value: "10M+", label: "Total Downloads", numericTarget: 10, suffix: "M+" },
  { value: "25+", label: "Products Shipped", numericTarget: 25, suffix: "+" },
  { value: "99.9%", label: "Service Uptime", numericTarget: 99.9, suffix: "%" },
  { value: "24/7", label: "Support Window", numericTarget: 24, suffix: "/7" },
];

const processSteps = [
  {
    title: "Discover",
    text: "We align goals, audience, and product metrics before writing production code.",
  },
  {
    title: "Design",
    text: "We shape UX flows and monetization paths that balance usability and conversion.",
  },
  {
    title: "Develop",
    text: "Rapid, test-ready delivery with optimized build pipelines and release discipline.",
  },
  {
    title: "Scale",
    text: "Post-launch tuning, analytics loops, and LiveOps support to compound growth.",
  },
];

const Home = () => {
  useScrollReveal();
  const [slides, setSlides] = useState(defaultSlides);
  const [about, setAbout] = useState(defaultAbout);
  const [ourStory, setOurStory] = useState(defaultOurStory);
  const [statsAnimated, setStatsAnimated] = useState(false);
  const [statValues, setStatValues] = useState(stats.map(() => 0));

  useEffect(() => {
    const unsubscribeSlides = onSnapshot(
      doc(db, "home", "slides"),
      (snapshot) => {
        if (snapshot.exists()) {
          setSlides(snapshot.data().slides || defaultSlides);
        } else {
          setSlides(defaultSlides);
        }
      }
    );

    const unsubscribeAbout = onSnapshot(doc(db, "home", "about"), (snapshot) => {
      if (snapshot.exists()) {
        setAbout(snapshot.data());
      } else {
        setAbout(defaultAbout);
      }
    });

    const unsubscribeOurStory = onSnapshot(
      doc(db, "home", "ourStory"),
      (snapshot) => {
        if (snapshot.exists()) {
          setOurStory(snapshot.data());
        } else {
          setOurStory(defaultOurStory);
        }
      }
    );

    return () => {
      unsubscribeSlides();
      unsubscribeAbout();
      unsubscribeOurStory();
    };
  }, []);

  useEffect(() => {
    const statsSection = document.querySelector(".stats-strip");
    if (!statsSection) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setStatsAnimated(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(statsSection);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!statsAnimated) {
      return undefined;
    }

    const duration = 1600;
    const frameMs = 16;
    const totalFrames = Math.max(1, Math.round(duration / frameMs));
    let frame = 0;

    const timer = setInterval(() => {
      frame += 1;
      const progress = Math.min(frame / totalFrames, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      const nextValues = stats.map((item) => item.numericTarget * eased);
      setStatValues(nextValues);

      if (progress >= 1) {
        clearInterval(timer);
      }
    }, frameMs);

    return () => clearInterval(timer);
  }, [statsAnimated]);

  const heroContent = slides[0] || defaultSlides[0];

  // Smooth typewriter effect — only restarts if title actually changes
  const fullTitle = heroContent.title || "";
  const [typedTitle, setTypedTitle] = useState("");
  const [typingDone, setTypingDone] = useState(false);
  const lastTypedTitle = useRef("");
  useEffect(() => {
    if (lastTypedTitle.current === fullTitle) return; // skip if same text
    lastTypedTitle.current = fullTitle;
    setTypedTitle("");
    setTypingDone(false);
    let i = 0;
    let lastTime = null;
    let raf;
    const charDelay = (ch) => {
      if (ch === " ") return 80;
      if (",.!?".includes(ch)) return 180;
      return 40 + Math.random() * 30;
    };
    let delay = 0;
    const tick = (timestamp) => {
      if (!lastTime) lastTime = timestamp;
      const elapsed = timestamp - lastTime;
      if (elapsed >= delay) {
        lastTime = timestamp;
        i++;
        setTypedTitle(fullTitle.slice(0, i));
        if (i < fullTitle.length) {
          delay = charDelay(fullTitle[i]);
          raf = requestAnimationFrame(tick);
        } else {
          setTypingDone(true);
        }
      } else {
        raf = requestAnimationFrame(tick);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [fullTitle]);

  return (
    <div className="home">
      <SEO title="Innovative Mobile Gaming Solutions" description="MacroMatrix builds high-performance mobile games and apps. Full-cycle development, LiveOps, and growth strategy for iOS and Android." path="/" />
      <section className="hero">
        <div className="hero-glow glow-left" />
        <div className="hero-glow glow-right" />
        <div className="hero-content reveal">
          <p className="hero-kicker">MacroMatrix Private Limited</p>
          <h1>{typedTitle}{!typingDone && <span className="hero-cursor">|</span>}</h1>
          <p>{heroContent.description}</p>
          <div className="hero-cta-row">
            <Link to="/apps" className="hero-cta-primary">
              Explore Apps
            </Link>
            <Link to="/contact" className="hero-cta-secondary">
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <section className="stats-strip reveal">
        {stats.map((item, index) => (
          <div className="stat-item" key={item.label}>
            <h3>
              {item.numericTarget % 1 === 0
                ? Math.round(statValues[index])
                : statValues[index].toFixed(1)}
              {item.suffix}
            </h3>
            <p>{item.label}</p>
          </div>
        ))}
      </section>

      <section className="about reveal">
        <h2>{about.title}</h2>
        <p>{about.content}</p>
      </section>

      <section className="features reveal">
        <h2>Why Teams Choose MacroMatrix</h2>
        <div className="feature-grid">
          {featureItems.map((item) => (
            <article
              className="feature-card"
              key={item.title}
              onMouseMove={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                const x = (e.clientX - r.left) / r.width - 0.5;
                const y = (e.clientY - r.top) / r.height - 0.5;
                e.currentTarget.style.transform = `rotateX(${-y * 7}deg) rotateY(${x * 7}deg) translateY(-4px)`;
              }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = ""; }}
            >
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="featured-work reveal">
        <h2>Featured Work</h2>
        <p className="featured-subtitle">A snapshot of what we've shipped.</p>
        <div className="featured-grid">
          {[
            { icon: "🚀", title: "Galaxy Strike", cat: "Action Game", downloads: "2.4M+", grad: "linear-gradient(135deg,#1a1a2e,#0f3460)" },
            { icon: "💍", title: "Second Marriage", cat: "Social App", downloads: "50K+", grad: "linear-gradient(135deg,#f37498,#fb897e)" },
            { icon: "🏎️", title: "Drift Kings", cat: "Racing Game", downloads: "3.2M+", grad: "linear-gradient(135deg,#141e30,#243b55)" },
          ].map((p) => (
            <article key={p.title} className="featured-card">
              <div className="featured-thumb" style={{ background: p.grad }}>
                <span>{p.icon}</span>
              </div>
              <div className="featured-meta">
                <span className="featured-cat">{p.cat}</span>
                <h3>{p.title}</h3>
                <p>⬇ {p.downloads} downloads</p>
              </div>
            </article>
          ))}
        </div>
        <Link to="/portfolio" className="hero-cta-secondary" style={{ display: "inline-block", marginTop: 28 }}>
          View Full Portfolio →
        </Link>
      </section>

      <section className="process reveal">
        <h2>How We Build Winning Products</h2>
        <div className="process-grid">
          {processSteps.map((step, index) => (
            <article className="process-step" key={step.title}>
              <span className="step-index">0{index + 1}</span>
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="our-story reveal">
        <h2>{ourStory.title}</h2>
        <p>{ourStory.content}</p>
        <Link to="/company" className="read-more-button">
          Read More <span>&#10143;</span>
        </Link>
      </section>

      <section className="final-cta reveal">
        <h2>Ready to Launch Your Next Product?</h2>
        <p>
          Work with a team that blends design precision, engineering quality,
          and growth strategy into one execution path.
        </p>
        <div className="final-cta-row">
          <Link to="/contact" className="hero-cta-primary">
            Start a Project
          </Link>
          <Link to="/careers" className="hero-cta-secondary">
            Join Our Team
          </Link>
        </div>
      </section>

      <CookieConsent />
    </div>
  );
};

export default Home;
